import { CartUpdateEvent } from '@theme/events';

const ATTRIBUTE_DECISION = 'Factura con nombre';
const LEGACY_ATTRIBUTE_DECISION = 'Solicita factura con nombre';
const ATTRIBUTE_RUC = 'RUC';
const ATTRIBUTE_BUSINESS_NAME = 'Razón social';
const VALID_RUC = /^[0-9-]+$/;

class InvoicePreferenceController {
  constructor() {
    this.root = document.querySelector('[data-invoice-preference]');
    this.dialog = this.root?.querySelector('#InvoicePreferenceDialog');
    this.choiceStep = this.root?.querySelector('[data-invoice-step="choice"]');
    this.toggleOffStep = this.root?.querySelector('[data-invoice-step="toggle-off"]');
    this.detailsStep = this.root?.querySelector('[data-invoice-step="details"]');
    this.toggleContainer = this.root?.querySelector('[data-invoice-toggle-container]');
    this.toggleInput = this.root?.querySelector('[data-invoice-toggle]');
    this.form = this.root?.querySelector('[data-invoice-form]');
    this.rucInput = this.root?.querySelector('[data-invoice-ruc]');
    this.businessNameInput = this.root?.querySelector('[data-invoice-business-name]');
    this.error = this.root?.querySelector('[data-invoice-error]');
    this.globalStatus = this.root?.querySelector('#InvoicePreferenceGlobalStatus');
    this.state = {
      decision: this.root?.dataset.decision || '',
      ruc: this.root?.dataset.ruc || '',
      businessName: this.root?.dataset.businessName || '',
    };
    this.context = null;
    this.pendingProduct = null;
    this.opener = null;
    this.busy = false;

    if (!this.root || !(this.dialog instanceof HTMLDialogElement)) return;

    document.addEventListener('click', this.handleDocumentClick, true);
    document.addEventListener('submit', this.handleDocumentSubmit, true);
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('change', this.handleDialogChange);
    this.dialog.addEventListener('cancel', this.handleDialogCancel);
    this.dialog.addEventListener('close', this.handleDialogClose);
  }

  handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const buyNowButton = event.target.closest('[data-invoice-buy-now]');
    if (buyNowButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (this.busy) return;

      this.refreshStateFromSummary();

      try {
        this.pendingProduct = this.createProductRequest(buyNowButton);
      } catch (error) {
        this.open('buy-now', buyNowButton);
        this.showError(this.errorMessage(error));
        return;
      }

      if (this.hasValidDecision()) {
        this.completeBuyNowWithoutModal(buyNowButton);
      } else {
        this.open('buy-now', buyNowButton);
      }
      return;
    }

    const cartAction = event.target.closest('[data-invoice-cart-action]');
    if (cartAction instanceof HTMLElement) {
      event.preventDefault();
      this.refreshStateFromSummary();
      this.open('cart-edit', cartAction);
      return;
    }

    const checkoutButton = event.target.closest('[name="checkout"], [data-invoice-checkout]');
    if (!(checkoutButton instanceof HTMLElement) || !checkoutButton.closest('.cart__ctas')) return;

    this.refreshStateFromSummary();
    if (this.hasValidDecision()) return;

    event.preventDefault();
    event.stopPropagation();
    this.open('cart-checkout', checkoutButton);
  };

  handleDocumentSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    if (event.target.matches('[data-invoice-form]')) {
      event.preventDefault();
      this.saveAndContinue('Sí');
      return;
    }

    if (event.target.id !== 'cart-form') return;

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLElement) || submitter.getAttribute('name') !== 'checkout') return;

    this.refreshStateFromSummary();
    if (this.hasValidDecision()) return;

    event.preventDefault();
    this.open('cart-checkout', submitter);
  };

  handleDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target === this.dialog) {
      this.close();
      return;
    }

    if (event.target.closest('[data-invoice-close]')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-invoice-back]')) {
      if (this.context === 'cart-edit') {
        if (this.toggleInput instanceof HTMLInputElement) this.toggleInput.checked = false;
        this.showStep('toggle-off');
      } else {
        this.showStep('choice');
      }
      return;
    }

    if (event.target.closest('[data-invoice-save-no]')) {
      this.saveAndContinue('No');
      return;
    }

    const choice = event.target.closest('[data-invoice-choice]');
    if (!(choice instanceof HTMLElement)) return;

    if (choice.dataset.invoiceChoice === 'No') {
      this.saveAndContinue('No');
    } else if (choice.dataset.invoiceChoice === 'Sí') {
      this.showStep('details');
    }
  };

  handleDialogChange = (event) => {
    if (event.target !== this.toggleInput || !(this.toggleInput instanceof HTMLInputElement)) return;
    this.showStep(this.toggleInput.checked ? 'details' : 'toggle-off');
  };

  handleDialogCancel = (event) => {
    if (this.busy) {
      event.preventDefault();
    } else {
      this.pendingProduct = null;
      this.context = null;
    }
  };

  handleDialogClose = () => {
    this.setBusy(false);
    this.hideError();
    const opener = this.opener;
    this.opener = null;
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
  };

  open(context, opener) {
    if (!(this.dialog instanceof HTMLDialogElement) || this.busy) return;

    this.context = context;
    this.opener = opener;
    this.hideError();
    this.populateFields();

    const isCartEdit = context === 'cart-edit';
    if (this.toggleContainer instanceof HTMLElement) this.toggleContainer.hidden = !isCartEdit;
    if (this.toggleInput instanceof HTMLInputElement) this.toggleInput.checked = this.state.decision === 'Sí';
    this.showStep(isCartEdit ? (this.state.decision === 'Sí' ? 'details' : 'toggle-off') : 'choice');

    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (!(this.dialog instanceof HTMLDialogElement) || this.busy || !this.dialog.open) return;
    this.pendingProduct = null;
    this.context = null;
    this.dialog.close();
  }

  showStep(step) {
    if (
      !(this.choiceStep instanceof HTMLElement) ||
      !(this.toggleOffStep instanceof HTMLElement) ||
      !(this.detailsStep instanceof HTMLElement)
    ) return;

    const showDetails = step === 'details';
    this.choiceStep.hidden = step !== 'choice';
    this.toggleOffStep.hidden = step !== 'toggle-off';
    this.detailsStep.hidden = !showDetails;
    this.hideError();

    queueMicrotask(() => {
      const focusTarget = showDetails
        ? this.rucInput
        : step === 'toggle-off'
          ? this.toggleOffStep?.querySelector('[data-invoice-save-no]')
          : this.choiceStep?.querySelector('[data-invoice-choice="No"]');
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
    });
  }

  populateFields() {
    if (this.rucInput instanceof HTMLInputElement) {
      this.rucInput.value = this.state.ruc;
      this.rucInput.removeAttribute('aria-invalid');
    }
    if (this.businessNameInput instanceof HTMLInputElement) {
      this.businessNameInput.value = this.state.businessName;
      this.businessNameInput.removeAttribute('aria-invalid');
    }
  }

  refreshStateFromSummary() {
    const summaries = Array.from(document.querySelectorAll('[data-invoice-summary]'));
    const summary = summaries.find((element) => element instanceof HTMLElement);
    if (!(summary instanceof HTMLElement)) return;

    this.state = {
      decision: summary.dataset.invoiceDecision || '',
      ruc: summary.dataset.invoiceRuc || '',
      businessName: summary.dataset.invoiceBusinessName || '',
    };
    this.updateRootDataset();
  }

  hasValidDecision() {
    if (this.state.decision === 'No') return true;
    return this.state.decision === 'Sí' && Boolean(this.state.ruc.trim()) && Boolean(this.state.businessName.trim());
  }

  validateDetails() {
    if (!(this.rucInput instanceof HTMLInputElement) || !(this.businessNameInput instanceof HTMLInputElement)) {
      throw new Error('No se encontraron los campos de facturación. Recargá la página e intentá nuevamente.');
    }

    const ruc = this.rucInput.value.trim();
    const businessName = this.businessNameInput.value.trim();
    this.rucInput.toggleAttribute('aria-invalid', !ruc || !VALID_RUC.test(ruc));
    this.businessNameInput.toggleAttribute('aria-invalid', !businessName);

    if (!ruc || !businessName) {
      throw new Error('Completá el RUC y la Razón Social para continuar.');
    }
    if (!VALID_RUC.test(ruc)) {
      throw new Error('El RUC solo puede contener números y guion.');
    }

    return { ruc, businessName };
  }

  async saveAndContinue(decision) {
    if (this.busy) return;

    let nextState;
    try {
      nextState = decision === 'No' ? { decision: 'No', ruc: '', businessName: '' } : {
        decision: 'Sí',
        ...this.validateDetails(),
      };
    } catch (error) {
      this.showError(this.errorMessage(error));
      return;
    }

    this.setBusy(true);
    this.hideError();

    try {
      const cart = await this.updateCartAttributes(nextState);
      this.state = nextState;
      this.updateRootDataset();
      this.publishCartUpdate(cart);

      if (this.context === 'buy-now') {
        if (!this.pendingProduct) throw new Error('No se pudo recuperar el producto seleccionado. Cerrá el formulario e intentá nuevamente.');
        await this.addProduct(this.pendingProduct);
        this.goToCheckout();
        return;
      }

      if (this.context === 'cart-checkout') {
        this.goToCheckout();
        return;
      }

      this.setBusy(false);
      this.close();
    } catch (error) {
      this.setBusy(false);
      this.showError(this.errorMessage(error));
    }
  }

  async completeBuyNowWithoutModal(button) {
    if (!this.pendingProduct || this.busy) return;

    this.setBusy(true, button);
    try {
      await this.addProduct(this.pendingProduct);
      this.goToCheckout();
    } catch (error) {
      this.setBusy(false, button);
      this.open('buy-now', button);
      this.showError(this.errorMessage(error));
    }
  }

  createProductRequest(button) {
    const productForm = button.closest('product-form-component');
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    const container = button.closest('[ref="acceleratedCheckoutButtonContainer"]');

    if (!(form instanceof HTMLFormElement)) {
      throw new Error('No se encontró el formulario del producto. Recargá la página e intentá nuevamente.');
    }
    if (container instanceof HTMLElement && container.hidden) {
      throw new Error('La variante seleccionada no está disponible. Elegí otra opción.');
    }

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '').trim();
    const quantity = Number(formData.get('quantity') || productForm?.dataset.quantityDefault || 1);

    if (!variantId) throw new Error('Seleccioná una variante disponible para continuar.');
    if (!Number.isFinite(quantity) || quantity < 1) throw new Error('Seleccioná una cantidad válida para continuar.');

    formData.delete('sections');
    formData.delete('sections_url');
    return formData;
  }

  async updateCartAttributes(nextState) {
    const sectionIds = this.cartSectionIds();
    const payload = {
      attributes: {
        [ATTRIBUTE_DECISION]: nextState.decision,
        [LEGACY_ATTRIBUTE_DECISION]: '',
        [ATTRIBUTE_RUC]: nextState.ruc,
        [ATTRIBUTE_BUSINESS_NAME]: nextState.businessName,
      },
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(this.localizedRoute('cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await this.readJson(response);
    if (!response.ok || data.status || data.errors) throw new Error(this.apiError(data));
    return data;
  }

  async addProduct(formData) {
    const response = await fetch(this.localizedRoute('cart/add.js'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await this.readJson(response);
    if (!response.ok || data.status || data.errors) throw new Error(this.apiError(data));
    return data;
  }

  async readJson(response) {
    try {
      return await response.json();
    } catch (_error) {
      throw new Error('Shopify no devolvió una respuesta válida. Intentá nuevamente.');
    }
  }

  apiError(data) {
    if (typeof data.description === 'string') return data.description;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.errors === 'string') return data.errors;
    return 'No pudimos guardar los datos. Revisá tu conexión e intentá nuevamente.';
  }

  publishCartUpdate(cart) {
    if (!cart.sections) return;
    document.dispatchEvent(new CartUpdateEvent(cart, 'invoice-preference', {
      source: 'invoice-preference',
      itemCount: cart.item_count,
      sections: cart.sections,
    }));
  }

  cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
      .filter((value, index, values) => value && values.indexOf(value) === index)
      .slice(0, 5);
  }

  localizedRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}${path}`;
  }

  goToCheckout() {
    window.location.assign(this.localizedRoute('checkout'));
  }

  updateRootDataset() {
    if (!(this.root instanceof HTMLElement)) return;
    this.root.dataset.decision = this.state.decision;
    this.root.dataset.ruc = this.state.ruc;
    this.root.dataset.businessName = this.state.businessName;
  }

  setBusy(isBusy, sourceButton = null) {
    this.busy = isBusy;
    if (this.dialog instanceof HTMLDialogElement) this.dialog.setAttribute('aria-busy', String(isBusy));

    this.dialog?.querySelectorAll('button, input').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) control.disabled = isBusy;
    });

    const activeButton = sourceButton || this.opener?.closest?.('[data-invoice-buy-now]');
    if (activeButton instanceof HTMLButtonElement) {
      activeButton.disabled = isBusy;
      activeButton.toggleAttribute('aria-busy', isBusy);
      const label = activeButton.querySelector('[data-invoice-button-label]');
      if (label) label.textContent = isBusy ? 'Procesando…' : 'Comprar ahora';
    }

    const submitLabel = this.dialog?.querySelector('[data-invoice-submit-label]');
    if (submitLabel) submitLabel.textContent = isBusy ? 'Guardando…' : 'Guardar y continuar';
  }

  showError(message) {
    if (this.error instanceof HTMLElement) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    if (this.globalStatus instanceof HTMLElement) this.globalStatus.textContent = message;
  }

  hideError() {
    if (this.error instanceof HTMLElement) {
      this.error.textContent = '';
      this.error.hidden = true;
    }
    if (this.globalStatus instanceof HTMLElement) this.globalStatus.textContent = '';
  }

  errorMessage(error) {
    return error instanceof Error && error.message
      ? error.message
      : 'Ocurrió un error. Revisá tu conexión e intentá nuevamente.';
  }
}

new InvoicePreferenceController();
